/**
 * @fileoverview A view component responsible for rendering the order data grid.
 * This component displays the list of orders with columns, expandable details, and row-specific UI logic.
 * It is designed to be a "dumb" component that receives all its data and handlers as props from a parent controller.
 *
 * @author Web Analysis Team (Refactored)
 * @version 1.0.0
 */
import React, { useEffect, useRef, useState } from 'react';
import { FunctionField, TextField, Datagrid, useListContext } from 'react-admin';
import { Tooltip, TextField as MuiTextField } from '@mui/material';
import { dayjs as mydayjs } from '../../../../components/ex_dayjs';
import { STATUS_ENUM, DeliveryMethodColors, editColor } from '../../../../utils/enum';
// Import necessary components and contexts
import { OrderContext } from '../hooks/OrderContext';
import OrderDetails from './order-details';
import { ODatagridHeader } from '../controls/ODatagridHeader';
import StatusField from './status-field';
import OrderNotes from './order-notes';
import { dataProvider } from '../../../../DataProvider';

interface IData {
    [key: string]: any;
}

const OrderGrid: React.FC<IData> = (props) => {
    const {
        orderAccounts,
        filterConfig,
        appIO,
        editingOrders,
        setEditingOrders,
        effectData,
        setEffectData,
        isResale,
        usesStrOrderNumber,
        showSystemNotes,
        setShowSystemNotes,
        marketType,
        tags,
        mode,
        notify,
        expandedIds,
        setExpandedId,
        matchesOrderNumber,
        // get body
        body,
        ...restProps
    } = props;
    const { vendor, bus, bus_events } = appIO;
    if (!vendor) return null;

    const [processingState, setProcessingState] = useState<IData>({ haveReceipt: false, primary_order_number: '', validatePrimaryTimeout: null, reloadState: false });
    const refOrderDetails = useRef<any>();
    const { data } = useListContext();

    const handleInputPrimaryOrderNo = (props2, record) => {
        let valueInput = props2.target.value;
        let validatePrimaryTimeout;
        let previousRequest = { reject: () => { } };

        window.clearTimeout(validatePrimaryTimeout);
        previousRequest.reject();

        record.primary_account = '';
        record.primary_order_number = valueInput;

        setProcessingState(prev => ({ ...prev, haveReceipt: false }));//force re-render row

        record.primary_cost = '';
        record.penalty_cost = '';

        if (valueInput === '') {
            return;
        }

        validatePrimaryTimeout = window.setTimeout(function () {
            let cancellation = new Promise(function (resolve, reject) {
                previousRequest.reject = reject;
            });

            let queryData = dataProvider.fetchData(vendor.controller, 'SearchPrimary?receiptNo=' + valueInput);

            Promise.race([cancellation, queryData])
                .then(function (json: any) {
                    previousRequest.reject = () => { };
                    if (!json.data) {
                        setProcessingState(prev => ({ ...prev, haveReceipt: false }));
                        return;
                    }

                    record.primary_cost = json.data.TixPrice;
                    record.primary_account = json.MailUser;
                    // bus.emit(bus_events.receipt_available, json);

                    setProcessingState(prev => ({ ...prev, haveReceipt: true }));
                },
                function () {
                    previousRequest.reject = () => { };
                });
        }, 500);

    };

    /**
     * Validates that Primary Cost and Primary Account fields are populated when changing
     * order status from Processing to Unprocessed. Used for both row click navigation
     * and order saving validation.
     *
     * @param {Object} record - Current order record
     * @param {Object} previousStatus - Previous order status
     * @returns {Object} Validation result with isValid flag and missing fields array
     */
    const validateProcessingToUnprocessed = (record, previousStatus) => {
        // Only validate when changing from Processing to Unprocessed
        // Check if this is a status change from Processing (6, 12) to Unprocessed (0)
        let isOwner = record.processor === filterConfig.currUser;
        if (!isOwner) {
            console.log('User is not the owner of the order');
            return { isValid: true, missingFields: [] };
        }
        let isProcessing = [STATUS_ENUM.Processing, STATUS_ENUM.TNProcessing].includes(previousStatus);
        // More additional tags to skip validation
        const SKIP_TAGS = ['Waiting to Process', 'Problem', 'Offered Alts', 'Order Support'];
        const currentTags = record.tags ? record.tags.split(',').map((tag: any) => tag.trim()).filter((tag: any) => tag) : [];
        if (!isProcessing || currentTags.some(tag => SKIP_TAGS.includes(tag))) {
            return { isValid: true, missingFields: [] };
        }

        const missingFields = [];

        if (!record.primary_account || record.primary_account.trim() === '') {
            missingFields.push('[Primary Account]');
        }

        if (!record.primary_cost || parseFloat(record.primary_cost) <= 0) {
            missingFields.push('[Primary Cost]');
        }

        return {
            isValid: missingFields.length === 0,
            missingFields: missingFields
        };
    };

    const validateAllowNavigate = (record) => {
        const missingFields = [];
        if (!record.primary_account || record.primary_account.trim() === '') {
            missingFields.push('[Primary Account]');
        }

        if (!record.primary_cost || parseFloat(record.primary_cost) <= 0) {
            missingFields.push('[Primary Cost]');
        }

        return {
            isValid: missingFields.length === 0,
            missingFields: missingFields.join(' + ')
        };
    };

    const handleRowClick = async (id, basePath, record) => {
        let changed = !expandedIds.includes(id);
        //Set highlight
        if (changed) {
            // Validate current editing record before allowing navigation
            if (effectData.currentOrder) {
                const currentRecord = effectData.currentOrder;
                // Perform validation when changing from Processing to Unprocessed
                let previousStatus = window.currentOrder.internal_status;
                const validation = validateProcessingToUnprocessed(currentRecord, previousStatus);
                if (!validation.isValid) {
                    console.log('Validation failed on row click:', validation.missingFields, currentRecord, effectData.currentOrder);
                    // Check current input valuses before navigation
                    let allowNavigation = validateAllowNavigate(window.currentOrder);
                    if (!allowNavigation.isValid) {
                        notify(`# Cannot change order status to Unprocessed. Missing field(s): ${allowNavigation.missingFields}.`, { type: 'warning', autoHideDuration: 5000 });
                        record.requiresValidation = true; // Flag to indicate validation failure
                        return null; // Prevent navigation
                    }
                    // Auto-save logic: When validation fails, attempt to auto-save the order
                    console.log('Auto-saving order with primary fields before navigation:', currentRecord.order_number);
                    if (refOrderDetails.current && refOrderDetails.current.onSaveOrder) {
                        try {
                            refOrderDetails.current.setIsLoading(true);
                            let result = await refOrderDetails.current.onSaveOrder({ allowRevertStatus: true });

                            if (result.status === 'SUCCESS') {
                                data.map((item: any) => {
                                    if (item.id === result.data.id) {
                                        Object.assign(item, result.data);
                                        item.internal_status = currentRecord.currentStatus;
                                        item.currentStatus = currentRecord.currentStatus;
                                    }
                                    return item;
                                });
                                // revert order status
                                console.log('Auto-save ORDER result:', result.status);
                            } else {
                                console.log(`Auto-save: ${result?.status}:`, result?.message);
                                return null; // Prevent navigation
                            }

                        } catch (error) {
                            console.error('Auto-save failed or loading notes failed:', error.message);
                            return null;
                        }
                        finally {
                            refOrderDetails.current.setIsLoading(false);
                        }
                    }
                }
            }
            // If the record allows revert status, emit the event with the new status
            let status = (!!record.allowRevertStatus || record.requiresValidation) ? record.internal_status : String(record.internal_status);
            bus.emit(bus_events.grid_selection_changed, record, status);
            setExpandedId([record.id]);
        }
        // prevent URL change
        return null;
    };

    // Move setEditingOrders out configRowUI to useEffect to avoid react render issues
    useEffect(() => {
        // Check if any order in editingOrders has rowData
        const needsCleanup = editingOrders.some(order => !!order.rowData);
        if (needsCleanup) {
            // If so, create a new array and remove rowData
            const cleanedOrders = editingOrders.map((order: any) => {
                // Only process orders that have rowData
                if (order.rowData) {
                    // delete rowData from order:
                    const { rowData: _rowData, ...rest } = order;
                    return rest;
                }
                return order;
            });
            // Safely update state after render
            setEditingOrders(cleanedOrders);
        }
        // This hook will run again whenever editingOrders changes
    }, [editingOrders, setEditingOrders]);

    const configRowUI = (order, index) => {
        let config = { position: 'relative' };
        //Fixed issue - react-render: if order is null or has no order_number, return empty config
        if (order === null || order.id === 0 || !order.order_number) return config;

        if (editingOrders.length) {
            let editOrders = editingOrders.filter((x: any) => matchesOrderNumber(x, order.order_number)).sort((a: any, b: any) => {
                // Sort by timestamp, most recent first
                return new Date(b.timeStamp) - new Date(a.timeStamp);
            });
            let editOrder = editOrders.length > 0 ? editOrders[0] : null;
            if (editOrder) {
                if (!!editOrder.rowData) {
                    // Override order properties with editOrder.rowData only for columns that exist in order
                    Object.keys(editOrder.rowData).forEach(key => {
                        if (order.hasOwnProperty(key)) {
                            order[key] = editOrder.rowData[key];
                        }
                    });
                }
                config.padding = '5px';
                config['box-shadow'] = `0 0 10px 0 ${editColor[editOrder.boxData.Color - 1]}`;
                config['z-index'] = 2;
            }
        }
        if (index % 2 !== 0) {
            config['background-color'] = mode === 'dark' ? '#1a1a1a' : '#f9f9f9';
            config['z-index'] = 1;
        }

        return config;
    };

    return (
        <Datagrid
            bulkActionButtons={false}
            expand={
                <OrderContext.Provider value={{ showSystemNotes, setShowSystemNotes, isResale, usesStrOrderNumber }}>
                    <OrderDetails
                        ref={refOrderDetails}
                        appIO={appIO}
                        accounts={orderAccounts}
                        currUser={filterConfig.currUser}
                        effectData={effectData}
                        setEffectData={setEffectData}
                        marketType={marketType}
                        filterTags={tags}
                        processingState={processingState}
                        editingOrders={editingOrders}
                    />
                </OrderContext.Provider>
            }
            expandSingle
            rowClick={handleRowClick}
            rowSx={configRowUI}
            header={<ODatagridHeader vendor={vendor} />}
            body={body} // Important: this props is used to customize body rendering for nested children
            {...restProps}
        >
            <FunctionField source="vivid_account_id" label={'ACC'} render={record => isResale ? record.vivid_account : orderAccounts.find((acc: any) => acc.id === record.vivid_account_id)?.name} />
            <TextField source={usesStrOrderNumber ? 'str_order_number' : 'order_number'} label={'Order #'} />
            <FunctionField label='Order Date' source='order_date' render={record => {
                if (!record.order_date) return null;
                return <Tooltip title={mydayjs(record.order_date).format('MMM DD, YYYY hh:mm A')}>
                    <span>{mydayjs(record.order_date).format('MMM DD, YYYY')}</span>
                </Tooltip>;}
            }
            />
            <FunctionField label='CB Date' source='checkback_date' render={record => (
                record.checkback_date ? mydayjs(record.checkback_date).format('MMM DD, YYYY') : null)}
            />
            <FunctionField label='Event' source='event-venue-order_total' render={record => vendor.buildEventBlock(record)} sortBy={'event'} />
            <FunctionField label='Event Date' source='date' render={record => {
                if (!record.date) return null;
                return <>{mydayjs(record.date).format('MMM DD, YYYY')}<br />{mydayjs(record.date).format('hh:mm A')}</>;
            }}
            />
            <FunctionField source="primary_order_number-primary_account" label={'Primary #'} render={record =>
                <Tooltip title={`${record.primary_order_number} ${record.primary_account}`}>
                    <span>
                        {parseInt(record.internal_status) === STATUS_ENUM.Processing && record.processor === filterConfig.currUser &&
                            <MuiTextField variant='outlined' size='small' margin='none' placeholder='order id...' sx={{ fontSize: '12px', borderColor: !processingState.haveReceipt ? 'red' : '' }}
                                onChange={(props) => handleInputPrimaryOrderNo(props, record)} value={record.primary_order_number} />
                            ||
                            (record.link_to_print_tickets && <a href={record.link_to_print_tickets} target='_blank' rel="noreferrer">{record.primary_order_number}</a> || <span>{record.primary_order_number}</span>)
                        }
                        <br /><span style={{ fontSize: '12px' }}>{record.primary_account}</span>
                    </span>
                </Tooltip>
            } />
            <FunctionField source="primary_cost" label={'Cost'} textAlign='right' render={record => (
                <span>{record.primary_cost ? `$${record.primary_cost}` : ''}{record.penalty_cost ? `($${record.penalty_cost})` : ''}</span>
            )} />
            <FunctionField label='Inhand Date' source='in_hand_date' render={record => {
                let dvMethod = DeliveryMethodColors.find((x: any) => x.id === record.delivery_method);
                let currentDatePST = mydayjs().tz('America/Los_Angeles').format('YYYY-MM-DD hh:mm:ss A');
                let daysToInHand = mydayjs(record.in_hand_date).diff(mydayjs(currentDatePST), 'day', true);
                let methodNameColor = daysToInHand < 2 ? '#d9534f' : daysToInHand < 5 ? '#f0ad4f' : '';
                if (!record.in_hand_date) return null;
                return (
                    <>
                        <span style={{ backgroundColor: dvMethod?.color, fontSize: '11px' }} className='custom-Tag'>{dvMethod?.name || 'PDF'}</span>
                        {daysToInHand < 5 && <span className='custom-Tag' style={{ backgroundColor: methodNameColor, marginLeft: '2px' }}>{mydayjs(record.in_hand_date).format('MMM DD, YYYY')}</span>
                            || <span style={{ marginLeft: '2px' }}>{mydayjs(record.in_hand_date).format('MMM DD, YYYY')}</span>}
                    </>
                );
            }} />
            <StatusField label='Status' appIO={appIO} source='internal_status-delivery_method' mydayjs={mydayjs}
                currUser={filterConfig.currUser}
                setProcessingState={setProcessingState} dataProvider={dataProvider} setEffectData={setEffectData} />
            <OrderNotes label="Notes" source='order_notes' sortable={false} editingOrders={editingOrders} vendor={vendor} usesStrOrderNumber={usesStrOrderNumber} />
        </Datagrid>
    );
};

export default OrderGrid;
